import { Leaf, Heart, Sparkle, UsersThree } from "@phosphor-icons/react";
import { useEffect } from "react";

const values = [
  { icon: Heart, title: "Care in every load", desc: "We treat your clothes like our own — checked, sorted and handled gently from pick up to delivery." },
  { icon: Leaf, title: "Kind to the planet", desc: "Biodegradable, eco-friendly detergents, cold-water washing where possible, and energy-efficient machines." },
  { icon: Sparkle, title: "Spotless standards", desc: "Every item is inspected before it leaves us. If it's not fresh, it doesn't go back in the basket." },
  { icon: UsersThree, title: "Local & family-run", desc: "We're your neighbours here in NSW, and we love giving locals more time for the things that matter." },
];

export default function About() {
  useEffect(() => { document.title = "About Us | Zaekids Laundromat — Eco-Friendly Laundry NSW"; }, []);
  return (
    <div data-testid="about-page" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="font-script text-3xl font-bold text-brand-yellow-dark">Our Story</span>
          <h1 className="mt-2 text-4xl font-black tracking-tight text-brand-green sm:text-5xl">Born from busy laundry baskets. Built by family.</h1>
          <div className="mt-6 space-y-4 leading-relaxed text-slate-700">
            <p>Zaekids Laundromat started with a simple idea: laundry shouldn't steal your weekend.</p>
            <p>As a busy family juggling work, kids and everyday life in NSW, we know just how quickly the laundry can pile up — and how much precious time it can take away from the people and moments that matter most. We knew there had to be a better way, so we created the laundry service we always wished existed: one that comes to <em>you</em>.</p>
            <p>But Zaekids is more than just a business name. It represents our family.</p>
            <p><span className="font-bold text-brand-green">Zaekids Laundromat</span> is a family business inspired by four siblings, each sharing "Zae" at the beginning of their names. They are the heart behind the name and the inspiration for building something meaningful that our family can grow together and one day proudly call their own.</p>
            <p>Today, we provide professional wash, dry, fold, ironing and steaming services for households and businesses across Western Sydney, with convenient pick-up and delivery right to your door.</p>
            <p>Every load is handled with care, because we understand that we're not simply washing clothes — we're giving our customers something valuable back: their time.</p>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 rotate-3 rounded-[3rem] bg-brand-yellow" />
          <img src="https://images.unsplash.com/photo-1699797467199-6bdf301649e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Freshly folded laundry" className="relative aspect-[4/3] w-full -rotate-1 rounded-[3rem] object-cover shadow-xl" />
        </div>
      </div>

      <div className="mt-10 rounded-[3rem] bg-brand-green p-10 text-center text-white md:p-14">
        <div className="space-y-1 text-xl font-extrabold sm:text-2xl">
          <p>More time for family.</p>
          <p>More time to relax.</p>
          <p>More time for the things that matter.</p>
        </div>
        <p className="mt-6 font-script text-4xl text-brand-yellow sm:text-5xl">Clean clothes. Happy life.</p>
        <p className="mt-3 text-green-100">It's more than our tagline — it's the reason Zaekids Laundromat began.</p>
      </div>

      <div className="mt-20">
        <h2 className="text-center text-3xl font-bold tracking-tight text-brand-green md:text-4xl">What we stand for</h2>
        <div className="mt-10 grid gap-8 sm:grid-cols-2">
          {values.map((v) => (
            <div key={v.title} className="card-lift flex gap-5 rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-soft-green text-brand-green">
                <v.icon size={30} weight="duotone" />
              </span>
              <div>
                <h3 className="text-lg font-extrabold text-brand-green">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-20 rounded-[3rem] bg-soft-green p-10 md:p-14">
        <div className="flex items-center gap-3">
          <Leaf size={36} weight="duotone" className="text-brand-green" />
          <h2 className="text-3xl font-bold tracking-tight text-brand-green">Our eco-friendly promise</h2>
        </div>
        <ul className="mt-6 grid gap-4 text-slate-700 md:grid-cols-2">
          <li className="rounded-2xl bg-white p-5">🌿 Plant-based, biodegradable detergents that are gentle on skin and waterways</li>
          <li className="rounded-2xl bg-white p-5">💧 High-efficiency machines that use up to 40% less water per load</li>
          <li className="rounded-2xl bg-white p-5">☀️ Cold-water washing where fabric care allows, saving energy every cycle</li>
          <li className="rounded-2xl bg-white p-5">🚚 Route-optimised pick ups & deliveries to keep our footprint small</li>
        </ul>
      </div>
    </div>
  );
}
